/**
 * Paelode invoices: the company's own bill from Paelode Systems (OPEN-212 PR C, DEC-387 D2).
 * A list of sent, paid and voided invoices; one opens with its PDF and, while it is owed,
 * "Pay by bank" (Stripe's hosted page). A bank payment shows as settling until it clears.
 * VIEW_PLATFORM_BILL: tenant admin and the billing roles (the API enforces it).
 */
import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { formatUsdFromCents } from '../lib/money';
import {
  usePaelodeInvoice,
  usePaelodeInvoicePdf,
  usePaelodeInvoices,
  usePaelodePaySession,
  type PaelodeInvoice,
} from '../features/billing/paelode-bill-hooks';

const cell: React.CSSProperties = { padding: '6px 8px' };
const muted: React.CSSProperties = { color: 'var(--color-text-secondary)', fontSize: '13px' };

export function statusText(inv: PaelodeInvoice): string {
  if (inv.status === 'VOID') return 'Cancelled';
  if (inv.status === 'PAID') return inv.paidOn === null ? 'Paid' : `Paid ${inv.paidOn}`;
  if (inv.pendingCents > 0) return 'Bank payment settling';
  return 'Owed';
}

export function PaelodeInvoicesPage(): React.JSX.Element {
  const { data, isLoading, isError } = usePaelodeInvoices();
  const navigate = useNavigate();
  const rows = data?.invoices ?? [];
  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 4px' }}>Paelode invoices</h2>
      <p style={{ ...muted, marginTop: 0 }}>Your bills from Paelode Systems for this software.</p>
      {isLoading ? (
        <p style={muted}>Loading…</p>
      ) : isError ? (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load your Paelode invoices.
        </p>
      ) : rows.length === 0 ? (
        <p style={muted}>No invoices yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ textAlign: 'left', ...muted }}>
              <th style={cell}>Invoice</th>
              <th style={cell}>Issued</th>
              <th style={cell}>Due</th>
              <th style={{ ...cell, textAlign: 'right' }}>Total</th>
              <th style={{ ...cell, textAlign: 'right' }}>Owed</th>
              <th style={cell}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (
              <tr
                key={i.id}
                data-testid={`paelode-invoice-${i.id}`}
                onClick={(): void => {
                  void navigate(`/paelode-invoices/${i.id}`);
                }}
                style={{ cursor: 'pointer', borderTop: '1px solid var(--color-border-primary)' }}
              >
                <td style={cell}>{i.invoiceNumber}</td>
                <td style={cell}>{i.issueDate}</td>
                <td style={cell}>{i.dueDate}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{formatUsdFromCents(i.totalCents)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  {formatUsdFromCents(i.balanceCents)}
                </td>
                <td style={cell}>{statusText(i)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function PaelodeInvoicePage(): React.JSX.Element {
  const { id = '' } = useParams();
  const [search] = useSearchParams();
  const { data, isLoading, isError } = usePaelodeInvoice(id);
  const pdf = usePaelodeInvoicePdf(id);
  const pay = usePaelodePaySession(id);
  const [error, setError] = React.useState<string | null>(null);

  if (isLoading) return <p style={{ padding: '24px', ...muted }}>Loading…</p>;
  if (isError || data === undefined) {
    return (
      <p role="alert" style={{ padding: '24px', color: 'var(--color-danger)' }}>
        That invoice was not found.
      </p>
    );
  }
  const inv = data.invoice;
  return (
    <div style={{ padding: '24px', maxWidth: '640px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 4px' }}>
        Invoice {inv.invoiceNumber}
      </h2>
      <p style={{ ...muted, marginTop: 0 }}>
        Issued {inv.issueDate} · due {inv.dueDate} · {statusText(inv)}
      </p>
      {search.get('paid') === '1' && inv.status === 'SENT' && (
        <p data-testid="paelode-pay-submitted" style={{ fontSize: '14px' }}>
          Thank you. Your bank payment is on its way; it usually settles in about 4 business days.
        </p>
      )}
      <table style={{ borderCollapse: 'collapse', fontSize: '14px', margin: '8px 0 16px' }}>
        <tbody>
          <tr>
            <td style={{ ...cell, ...muted }}>Total</td>
            <td style={{ ...cell, textAlign: 'right' }}>{formatUsdFromCents(inv.totalCents)}</td>
          </tr>
          <tr>
            <td style={{ ...cell, ...muted }}>Paid</td>
            <td style={{ ...cell, textAlign: 'right' }}>
              {formatUsdFromCents(inv.amountPaidCents)}
            </td>
          </tr>
          <tr>
            <td style={{ ...cell, fontWeight: 600 }}>Owed</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>
              {formatUsdFromCents(inv.balanceCents)}
            </td>
          </tr>
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {inv.hasPdf && (
          <button
            type="button"
            disabled={pdf.isPending}
            onClick={(): void =>
              pdf.mutate(undefined, {
                onSuccess: (link) => window.open(link.url, '_blank', 'noopener'),
                onError: () => setError('Could not open the PDF. Try again.'),
              })
            }
          >
            Download PDF
          </button>
        )}
        {data.canPayOnline && (
          <button
            type="button"
            disabled={pay.isPending}
            onClick={(): void =>
              pay.mutate(
                {},
                {
                  onSuccess: (s) => window.location.assign(s.url),
                  onError: () => setError('Could not start the payment. Try again in a minute.'),
                },
              )
            }
          >
            {pay.isPending ? 'Opening…' : 'Pay by bank'}
          </button>
        )}
      </div>
      {data.canPayOnline && (
        <p style={{ ...muted, fontSize: '12px' }}>
          You pay from your bank account (ACH) on Stripe&apos;s secure page. No fee to you.
        </p>
      )}
      {error !== null && (
        <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '14px' }}>
          {error}
        </p>
      )}
    </div>
  );
}
